/**
 * CE QUI DÉCIDE DE LA PAGE « DONNÉES DU COMPTEUR » v2 (DEV-1508 lot 3c).
 *
 * INTENTION : un client qui ouvre la page de données de son compteur Enedis,
 * interrupteur armé, doit voir EXACTEMENT la page que l'adresse ombre montre.
 * Pas « la même vue à quelques détails près » : la même page, au mot près.
 *
 * Les trois écarts vus en direct sur staging le 11/09/2026 venaient tous de la
 * même cause : chacune des deux entrées répondait à sa manière à une question
 * que personne n'avait posée en un seul endroit.
 *
 *  • « suis-je la page v2 ? » — écrit à la main dans `PageSourceData.vue`, et
 *    inconnu du store des graphiques, qui continuait donc d'écrire la période
 *    héritée dans l'URL de la route standard ;
 *  • « quel est mon contexte ? » — 365 jours bornés par la première donnée
 *    détenue sur l'ombre, la période de trois mois du store sur la standard ;
 *  • « quelle bande de complétude ? » — calculée des séries chargées sur
 *    l'ombre, calculée des lignes des indicateurs KPI sur la standard, sur un
 *    autre référentiel de jours et d'un autre jeu de séries : d'où le
 *    « 0 / 93 jours mesurés (0 %) » affiché alors que le graphe Énergie montrait
 *    des mesures du 11 au 28/06.
 *
 * Ce module porte les deux premières réponses, UNE fois. La troisième n'a pas
 * de réponse ici, et c'est le remède : la bande ne se calcule plus qu'à un seul
 * endroit, dans la vue, à partir des séries qu'elle a elle-même chargées
 * (`SourceDataEnedisView.vue`). Un écran appelant ne peut plus en servir une
 * autre — la prop qui le permettait n'existe plus.
 */
import { SOURCES_TYPES } from "@core/constants"
import { formatDate } from "@core/helpers/format.helper"
import {
    bornesDesSensDuPdl,
    contexteBornePar,
    premiereDonneeDetenue,
} from "@modules/data/utils/informationsPdl"
import type { InformationPDL, VariableDataV2Servie } from "@types"
import type { DirectionEnedis } from "@modules/data/services/sourceDataEnedis.service"

/**
 * Les jours du contexte de la page « Données du compteur » À L'OUVERTURE :
 * trois mois, comme la maquette (DEV-1508 lot 5c, écart B3, décision Q1 du
 * pilote du 15/09).
 *
 * L'année n'a pas disparu : elle est à un clic, sur le raccourci « 1 an ».
 */
export const JOURS_DU_CONTEXTE = 92

/**
 * LES RACCOURCIS DE CONTEXTE (spec v1.4 §2.2 pt 2, décision Q2 du 15/09).
 *
 * Ce ne sont PAS les « fenêtres préréglées » de la fenêtre REGARDÉE, écartées
 * le 09/09 : celles-là auraient remplacé le cliquer-glisser par des boutons.
 * Ceux-ci changent le CONTEXTE, c'est-à-dire la période CHARGÉE — la fin reste
 * au dernier jour dû, le début recule, et la fenêtre regardée revient au
 * contexte entier. Le brush reste le seul moyen de resserrer.
 */
export const RACCOURCIS_DE_CONTEXTE: Array<{
    jours: number
    libelle: string
}> = [
    { jours: 1, libelle: "1 jour" },
    { jours: 7, libelle: "7 jours" },
    { jours: 31, libelle: "1 mois" },
    { jours: 92, libelle: "3 mois" },
    { jours: 183, libelle: "6 mois" },
    { jours: 366, libelle: "1 an" },
    { jours: 730, libelle: "2 ans" },
]

/**
 * LE PLAFOND DU CONTEXTE, EN JOURS : deux ans.
 *
 * C'est le DÛ le plus court de la page (`PLAFOND_JOURS.daily` du lecteur, 730
 * jours pour les courbes et la puissance). L'énergie et les index vont plus
 * loin (1 095 jours, `PLAFOND_JOURS_INDEX_ENERGIE`), mais un contexte plus
 * large que le dû le plus court peindrait « Manquant » sur des jours qu'aucun
 * onglet de puissance ne peut servir : le contexte est partagé par tous les
 * onglets, il se plafonne donc sur le plus contraint.
 */
export const PLAFOND_JOURS_DU_CONTEXTE = 730

/** Une source, réduite aux deux seuls champs qui disent son type. */
export type SourcePourLaPageV2 = {
    idtype?: string | null
    type?: string | null
} | null

/**
 * Vrai quand la route STANDARD d'une source doit rendre la page v2.
 *
 * L'interrupteur est le PREMIER terme, et c'est l'engagement E11 : désarmé, la
 * réponse est non sans même regarder la source, aucune branche v2 n'est prise,
 * aucun appel de plus ne part, la page reste strictement celle d'avant.
 *
 * Le test d'identité porte sur `idtype` d'abord : dans le store la source est
 * fusionnée `{...sourceDapi, ...sourceLegacy}` et le `type` legacy, souvent
 * null, écrase celui calculé par dapi. Il ne porte QUE sur `pdl` : d'autres
 * types (ELECSAVOIE, CPCU…) partagent des chemins de graphes avec le PDL, ils ne
 * partagent pas cette page.
 */
export function laPageCompteurV2EstServie(params: {
    arme: boolean
    source: SourcePourLaPageV2
}): boolean {
    if (!params.arme) return false
    const s = params.source
    if (!s) return false
    return s.idtype === SOURCES_TYPES.PDL || s.type === SOURCES_TYPES.PDL
}

/**
 * Le jour civil LOCAL d'une date, ramené à midi UTC pour compter en jours.
 *
 * ⚠️ Compter les jours en soustrayant des millisecondes à une date LOCALE se
 * trompe d'un jour dès qu'on traverse un changement d'heure : reculer de 182
 * jours depuis le 10/09 tombait le 11/03 à 23 h, donc sur le 11/03, au lieu du
 * 12/03 — un contexte de « 6 mois » large de 184 jours au lieu de 183. En été
 * comme en hiver, un jour UTC vaut exactement 86 400 000 ms ; le jour CIVIL, lui,
 * se lit sur l'horloge du client, et c'est celui-là qu'on ancre.
 */
function jourCivilUtc(d: Date): number {
    return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())
}

/** Millisecondes UTC → YYYY-MM-DD. */
function ymd(ms: number): string {
    return new Date(ms).toISOString().slice(0, 10)
}

/**
 * LE JOUR DE LECTURE DE LA PAGE (lot 6a, revue du 21/09).
 *
 * LE DÉFAUT CORRIGÉ : la vue repliait sur `new Date().toISOString().slice(0,
 * 10)`, c'est-à-dire le jour UTC, et AUCUNE page de production ne lui passe de
 * jour (`PageSourceDonneesCompteur.vue`, `PageSourceData.vue`). Entre minuit et
 * 2 h à Paris, ce jour est donc la VEILLE : le dernier jour dû d'un flux recule
 * d'un jour, et le jour le plus récent du contexte — qui n'est pas encore dû —
 * bascule de « Attendu » à « Manquant ». Chaque nuit, sur un compteur sain.
 *
 * LE FUSEAU EST CELUI DE TOUTE L'APPLICATION, et il n'est pas recalculé ici :
 * `formatDate` (`core/helpers/format.helper.ts`) tient déjà l'`Europe/Paris`
 * par défaut sur toutes les dates affichées. Un second calcul de fuseau dans ce
 * module finirait par diverger du premier.
 *
 * ⚠️ À NE PAS CONFONDRE avec `jourCivilUtc` ci-dessus, qui ancre le CONTEXTE
 * sur le jour civil du NAVIGATEUR. Les deux coïncident sur le poste d'un
 * client français, qui est le seul parc servi ; les accorder pour de bon
 * demanderait de porter le fuseau jusque dans le contexte, ce qui appartient à
 * l'écran appelant et n'est pas de ce lot.
 */
export function jourDeLecture(maintenant: Date = new Date()): string {
    return formatDate(maintenant, "YYYY-MM-DD")
}

/**
 * LE CONTEXTE de la page « Données du compteur », quelle que soit l'entrée.
 *
 * Les `jours` derniers jours dus, bornés au début par la première donnée
 * DÉTENUE, et plafonnés par le dû (`PLAFOND_JOURS_DU_CONTEXTE`).
 *
 * POURQUOI TROIS MOIS À L'OUVERTURE, ET PLUS UNE ANNÉE (lot 5c, écart B3) : le
 * niveau se déduit de la largeur regardée (`niveauPourFenetre` : natif ≤ 14 j,
 * horaire de 15 à 92, journalier au-delà). À 365 jours, la page ouvrait au
 * journalier, l'enveloppe minimum et maximum n'était jamais servie, et rien ne
 * ressemblait à la maquette. À 92 jours elle ouvre sur l'enveloppe horaire, et
 * l'année reste à un clic — avec l'enveloppe journalière découpée par 92 jours,
 * puisque c'est la fenêtre où le lecteur la dérive encore.
 *
 * Ce qui n'est PAS fait ici : borner la FIN à la dernière donnée reçue. La spec
 * plafonne la période par le DÛ, c'est-à-dire par les fenêtres de calcul du
 * lecteur, pas par la dernière mesure servie. Un compteur arrêté depuis le 28/06
 * doit montrer son arrêt, à sa date, dans la bande et dans la courbe — pas voir
 * sa période raccourcie jusqu'à le faire disparaître.
 *
 * Ce qui n'est PAS fait non plus : lire la période du store des graphiques. Elle
 * appartient au sélecteur de la page de données standard, que la v2 ne montre
 * pas. La route standard armée affichait « du 11/06/2026 au 11/09/2026 (tout le
 * contexte) », trois mois choisis par personne, là où l'ombre ouvrait sur un an.
 *
 * LA FIN EST LA VEILLE, JAMAIS AUJOURD'HUI (DEV-1508 lot 5a, écart D3).
 *
 * Aucun flux n'est dû le jour même : la journée n'est pas finie, et Enedis ne
 * dépose rien avant le lendemain. Le jour courant entrait pourtant dans le
 * contexte, sans mesure et sans cause, donc peint « Manquant » : un compteur
 * parfaitement à jour ne pouvait pas atteindre 100 %, et la bande accusait tous
 * les soirs d'un trou qui n'existait pas (C4 364/365, C2 360/365, C5 312/314,
 * tous trois observés en prod le 15/09).
 *
 * POURQUOI LA VEILLE, ET PAS `aujourd'hui − attendu_jours` : `attendu_jours` du
 * statut est un attendu AGRÉGÉ sur tous les flux du compteur (3 jours sur les
 * trois témoins réels, lu live le 15/09). L'employer ici amputerait la bande de
 * deux jours DUS pour un flux journalier attendu à J+1, et cacherait un retard
 * naissant. L'attendu PAR FLUX viendra avec le verdict permanent (lot 5a2) ;
 * d'ici là, la veille est le seul fait que personne ne discute.
 */
export function contexteDeLaPageCompteur(
    variablesServies: Partial<
        Record<DirectionEnedis, VariableDataV2Servie[]>
    > | null,
    maintenant: Date = new Date(),
    informations: InformationPDL | null = null,
    jours: number = JOURS_DU_CONTEXTE
): { debut: string; fin: string } {
    // Un raccourci ne peut pas demander plus que le dû, ni moins d'un jour : ce
    // qu'on ne peut pas charger ne s'affiche pas en « Manquant ».
    const largeur = Number.isFinite(jours)
        ? Math.min(Math.max(1, Math.trunc(jours)), PLAFOND_JOURS_DU_CONTEXTE)
        : JOURS_DU_CONTEXTE
    const fin = jourCivilUtc(maintenant) - 86_400_000
    const debut = fin - (largeur - 1) * 86_400_000
    return contexteBornePar(
        { debut: ymd(debut), fin: ymd(fin) },
        premiereDonneeDetenue(
            variablesServies ?? {},
            bornesDesSensDuPdl(informations)
        )
    )
}
